// Sky and Water Scene Module
import * as THREE from 'three';
import { Water } from 'three/addons/objects/Water.js';
import { Sky } from 'three/addons/objects/Sky.js';

// Production logging utility
const log = {
    debug: (message, ...args) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(message, ...args);
        }
    },
    error: (message, ...args) => console.error(message, ...args),
    warn: (message, ...args) => console.warn(message, ...args)
};

export class SkyWaterScene {
    constructor(scene, renderer) {
        this.scene = scene;
        this.renderer = renderer;
        this.water = null;
        this.sky = null;
        this.waterCompiled = false;
        this.clock = new THREE.Clock();
        
        // Wave configuration
        this.waves = [
            {
                direction: 45,
                steepness: 0.02, // Reduced from 0.1
                wavelength: 7,
            },
            {
                direction: 306,
                steepness: 0.04, // Reduced from 0.2
                wavelength: 32,
            },
            {
                direction: 196,
                steepness: 0.06, // Reduced from 0.3
                wavelength: 59,
            },
        ];
        
        this.init();
    }
    
    init() {
        try {
            this.createWater();
            this.createSky();
            // this.setupLighting();
        } catch (error) {
            log.error('Error initializing sky and water scene:', error);
        }
    }
    
    createWater() {
        // Create water geometry
        const geometry = new THREE.BufferGeometry();
        
        const thetaSegments = 128;
        const phiSegments = 512;
        const thetaStart = 0;
        const thetaLength = Math.PI * 2;
        
        const indices = [];
        const vertices = [];
        const normals = [];
        const uvs = [];
        
        let radius = 0;
        let radiusStep = 1;
        const vertex = new THREE.Vector3();
        const uv = new THREE.Vector2();
        
        for (let j = 0; j <= phiSegments; j++) {
            for (let i = 0; i <= thetaSegments; i++) {
                const segment = thetaStart + (i / thetaSegments) * thetaLength;
                vertex.x = radius * Math.cos(segment);
                vertex.y = radius * Math.sin(segment);
                vertices.push(vertex.x, vertex.y, vertex.z);
                normals.push(0, 0, 1);
                uv.x = (vertex.x + 1) / 2;
                uv.y = (vertex.y + 1) / 2;
                uvs.push(uv.x, uv.y);
            }
            radiusStep = radiusStep * 1.01;
            radius += radiusStep;
        }
        
        for (let j = 0; j < phiSegments; j++) {
            const thetaSegmentLevel = j * (thetaSegments + 1);
            for (let i = 0; i < thetaSegments; i++) {
                const segment = i + thetaSegmentLevel;
                const a = segment;
                const b = segment + thetaSegments + 1;
                const c = segment + thetaSegments + 2;
                const d = segment + 1;
                indices.push(a, b, d);
                indices.push(b, c, d);
            }
        }
        
        geometry.setIndex(indices);
        geometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(vertices, 3)
        );
        geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
        geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
        
        // Create water material
        this.water = new Water(geometry, {
            textureWidth: 512,
            textureHeight: 512,
            waterNormals: new THREE.TextureLoader().load('./images/waternormals.jpg', function(texture) {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            }, undefined, function(error) {
                log.warn('Could not load water normal texture, using default:', error);
            }),
            sunDirection: new THREE.Vector3(),
            sunColor: 0x00e1ff,
            waterColor: 0x00e1ff,
            distortionScale: 8,
            fog: this.scene.fog !== undefined
        });
        
        this.water.rotation.x = -Math.PI / 2;
        this.water.position.y = 0; // Position water at ground level
        
        // Custom shader for Gerstner waves
        this.water.material.onBeforeCompile = (shader) => {
            shader.uniforms.offsetX = { value: 0 };
            shader.uniforms.offsetZ = { value: 0 };
            shader.uniforms.waveA = {
                value: [
                    Math.sin((this.waves[0].direction * Math.PI) / 180),
                    Math.cos((this.waves[0].direction * Math.PI) / 180),
                    this.waves[0].steepness,
                    this.waves[0].wavelength,
                ],
            };
            shader.uniforms.waveB = {
                value: [
                    Math.sin((this.waves[1].direction * Math.PI) / 180),
                    Math.cos((this.waves[1].direction * Math.PI) / 180),
                    this.waves[1].steepness,
                    this.waves[1].wavelength,
                ],
            };
            shader.uniforms.waveC = {
                value: [
                    Math.sin((this.waves[2].direction * Math.PI) / 180),
                    Math.cos((this.waves[2].direction * Math.PI) / 180),
                    this.waves[2].steepness,
                    this.waves[2].wavelength,
                ],
            };
            
            // Custom vertex shader with Gerstner waves
            shader.vertexShader = `
                uniform mat4 textureMatrix;
                uniform float time;
                
                varying vec4 mirrorCoord;
                varying vec4 worldPosition;
                
                #include <common>
                #include <fog_pars_vertex>
                #include <shadowmap_pars_vertex>
                #include <logdepthbuf_pars_vertex>
                
                uniform vec4 waveA;
                uniform vec4 waveB;
                uniform vec4 waveC;
                
                uniform float offsetX;
                uniform float offsetZ;
                
                vec3 GerstnerWave (vec4 wave, vec3 p) {
                    float steepness = wave.z;
                    float wavelength = wave.w;
                    float k = 2.0 * PI / wavelength;
                    float c = sqrt(9.8 / k);
                    vec2 d = normalize(wave.xy);
                    float f = k * (dot(d, vec2(p.x, p.y)) - c * time);
                    float a = steepness / k;
                    
                    return vec3(
                        d.x * (a * cos(f)),
                        d.y * (a * cos(f)),
                        a * sin(f)
                    );
                }
                
                void main() {
                    mirrorCoord = modelMatrix * vec4( position, 1.0 );
                    worldPosition = mirrorCoord.xyzw;
                    mirrorCoord = textureMatrix * mirrorCoord;
                    vec4 mvPosition =  modelViewMatrix * vec4( position, 1.0 );
                    
                    vec3 gridPoint = position.xyz;
                    vec3 tangent = vec3(1, 0, 0);
                    vec3 binormal = vec3(0, 0, 1);
                    vec3 p = gridPoint;
                    gridPoint.x += offsetX;
                    gridPoint.y -= offsetZ;
                    p += GerstnerWave(waveA, gridPoint);
                    p += GerstnerWave(waveB, gridPoint);
                    p += GerstnerWave(waveC, gridPoint);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( p.x, p.y, p.z, 1.0);
                    
                    #include <beginnormal_vertex>
                    #include <defaultnormal_vertex>
                    #include <logdepthbuf_vertex>
                    #include <fog_vertex>
                    #include <shadowmap_vertex>
                }
            `;
            
            shader.uniforms.size.value = 10.0;
            this.waterCompiled = true;
        };
        
        this.scene.add(this.water);
    }
    
    createSky() {
        const sun = new THREE.Vector3();
        this.sky = new Sky();
        this.sky.scale.setScalar(10000);
        this.scene.add(this.sky);
        
        const skyUniforms = this.sky.material.uniforms;
        
        skyUniforms["turbidity"].value = 10;
        skyUniforms["rayleigh"].value = 2;
        skyUniforms["mieCoefficient"].value = 0.005;
        skyUniforms["mieDirectionalG"].value = 0.8;
        
        const parameters = {
            elevation: 2,
            azimuth: 60,
        };
        
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        
        const updateSun = () => {
            const phi = THREE.MathUtils.degToRad(90 - parameters.elevation);
            const theta = THREE.MathUtils.degToRad(parameters.azimuth);
            
            sun.setFromSphericalCoords(1, phi, theta);
            
            this.sky.material.uniforms["sunPosition"].value.copy(sun);
            this.water.material.uniforms["sunDirection"].value.copy(sun).normalize();
            
            this.scene.environment = pmremGenerator.fromScene(this.sky).texture;
        };
        
        updateSun();
    }
    
    setupLighting() {
        // Add directional light for water reflections
        const waterLight = new THREE.DirectionalLight(0x00e1ff, 0.8);
        waterLight.position.set(50, 100, 50);
        waterLight.castShadow = true;
        waterLight.shadow.mapSize.width = 2048;
        waterLight.shadow.mapSize.height = 2048;
        waterLight.shadow.camera.near = 0.1;
        waterLight.shadow.camera.far = 500;
        waterLight.shadow.camera.left = -100;
        waterLight.shadow.camera.right = 100;
        waterLight.shadow.camera.top = 100;
        waterLight.shadow.camera.bottom = -100;
        this.scene.add(waterLight);
        this.scene.add(waterLight.target);
    }
    
    update(cameraPosition) {
        try {
            const delta = this.clock.getDelta();
            
            // Update water time
            if (this.water && this.waterCompiled) {
                this.water.material.uniforms['time'].value += delta;
                
                // Update water position to follow camera
                this.water.position.x = cameraPosition.x;
                this.water.position.z = cameraPosition.z;
                this.water.material.uniforms['offsetX'].value = cameraPosition.x;
                this.water.material.uniforms['offsetZ'].value = cameraPosition.z;
            }
        } catch (error) {
            log.error('Error updating sky and water scene:', error);
        }
    }
    
    // Wave calculation function for external use
    getWaveInfo(x, z, time) {
        const pos = new THREE.Vector3();
        const tangent = new THREE.Vector3(1, 0, 0);
        const binormal = new THREE.Vector3(0, 0, 1);
        
        Object.keys(this.waves).forEach((wave) => {
            const w = this.waves[wave];
            const k = (Math.PI * 2) / w.wavelength;
            const c = Math.sqrt(9.8 / k);
            const d = new THREE.Vector2(
                Math.sin((w.direction * Math.PI) / 180),
                -Math.cos((w.direction * Math.PI) / 180)
            );
            const f = k * (d.dot(new THREE.Vector2(x, z)) - c * time);
            const a = w.steepness / k;
            pos.x += d.y * (a * Math.cos(f));
            pos.y += a * Math.sin(f);
            pos.z += d.x * (a * Math.cos(f));
            tangent.x += -d.x * d.x * (w.steepness * Math.sin(f));
            tangent.y += d.x * (w.steepness * Math.cos(f));
            tangent.z += -d.x * d.y * (w.steepness * Math.sin(f));
            binormal.x += -d.x * d.y * (w.steepness * Math.sin(f));
            binormal.y += d.y * (w.steepness * Math.cos(f));
            binormal.z += -d.y * d.y * (w.steepness * Math.sin(f));
        });
        
        const normal = binormal.cross(tangent).normalize();
        return {
            position: pos,
            normal: normal,
        };
    }

    // Function to update floating objects (like the city model)
    updateFloatingObject(object, delta) {
        if (this.water && this.waterCompiled) {
            const time = this.water.material.uniforms['time'].value;
            const waveInfo = this.getWaveInfo(object.position.x, object.position.z, time);
            
            // Update the object's Y position to match wave height
            object.position.y = waveInfo.position.y;
            
            // Optionally, you can also rotate the object to match the wave normal
            // This creates a more realistic floating effect
            const quat = new THREE.Quaternion().setFromEuler(
                new THREE.Euler(waveInfo.normal.x, waveInfo.normal.y, waveInfo.normal.z)
            );
            object.quaternion.rotateTowards(quat, delta * 0.5);
        }
    }
}
